<template>
    <div :class="clContainer()">
        <div :class="clSub('image-container', 'form__input')">
            <spinner v-if="isLoading"></spinner>
            <template v-else>
                <img :class="clSub('image')" :src="imageSrc" />
                <p :class="clSub('input')" v-if="state == 'edit'">
                    <label>
                        <input type="file" ref="fileInput" :name="name" @change="imageChosen" :class="clSub('file')" accept="image/*" />
                        <slot name="fileLabel">
                            Bild ändern
                        </slot>
                    </label>
                </p>
            </template>
        </div>

        <div :class="clSub('errors')">
            <div v-for="(error, index) in errors" :class="clSub('error')" :key="index">
                {{ error }}
            </div>
        </div>

        <div class="croppie" ref="croppieContainer">
            <div class="croppie__header">Bildausschnitt wählen:</div>
            <vue-croppie 
                ref="croppieRef" 
                :enableOrientation="true"
                :enableResize="false"
                :boundary="{ width: 400, height: 400, }"
                :viewport="{ width: 400, height: 400, type: 'circle' }">
            </vue-croppie>
            <div class="croppie__controls">
                <button class="btn btn-next" @click="hideCroppie">Fertig</button>
            </div>
        </div>
    </div>
</template>

<script>
import Base from './Base';
import VueCroppie from 'vue-croppie';
import Uploader from '../../../modules/uploader';
import 'croppie/croppie.css';

export default {
    extends: Base,
    props: {
        typeClass: {
            type: String,
            default: 'image'
        },
        uploadUrl: {
            type: String
        },
        uploadHeaders: {
            type: Object
        },
        placeholderUrl: {
            type: String
        },
        useCroppie: {
            type: Boolean,
            default: true
        },
        hooks: {
            type: Object
        },
        srcLoader: {
            type: Function
        },
        chooseOnMounted: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            imageData: '',
            isLoading: false,
            loaded: false
        }
    },
    computed: {
        imageSrc() {
            if(this.imageData.length) {
                return this.imageData;
            }

            if(this.data && this.data instanceof Blob) {
                return URL.createObjectURL(this.data);
            } else if(this.srcLoader && !this.loaded && this.data) {
                this.isLoading = true;
                this.loaded = true;

                this.srcLoader(this.data).then(url => {
                    this.imageData = url;
                    this.isLoading = false;
                });
            }

            return this.data && this.data.length ? this.data : this.placeholderUrl;
        }
    },
    mounted() {
        if(this.$refs.fileInput && this.chooseOnMounted && !this.data) {
            this.$refs.fileInput.click();
        }
    },
    beforeDestroy() {
        if(this.data && this.data instanceof Blob) {
            return URL.revokeObjectURL(this.imageSrc);
        }
    },
    methods: {
        imageChosen(inputEvent) {
            if(inputEvent.target.files.length) {
                const file = inputEvent.target.files[0];

                if(this.uploadUrl) {
                    const uploader = new Uploader(inputEvent.target.name, null, this.uploadUrl);

                    if(this.uploadHeaders) {
                        uploader.uploadHeaders = this.uploadHeaders;
                    }

                    uploader.beforeUploading = () => {
                        this.hook('beforeUploading');
                    };
                    uploader.onError = () => {
                        this.hook('onError');
                    };
                    uploader.onUploaded = fileData => {
                        this.loadImage(fileData.url).then(() => {
                            this.showCroppie(fileData.url);
                            this.hook('uploadedImageLoaded');
                        });
                    };

                    uploader.upload(file);
                } else {
                    this.hook('beforeUploading');

                    if (!file.type.match(/^image.*/i)) {
                        this.setErrors([ 'Ungültiger Dateityp' ]);
                        this.hook('onError');
                    } else if(this.useCroppie) {
                        this.setErrors(null);
                        const reader = new FileReader();

                        reader.onload = progress => {
                            if(progress.target.result) {
                                this.showCroppie(progress.target.result);
                                this.hook('uploadedImageLoaded');
                            } else {
                                this.hook('onError');
                                console.error('Error while reading file');
                                console.log(progress);
                            }
                        };

                        reader.readAsDataURL(file);
                    } else {
                        this.setErrors(null);
                        this.data = file;
                        this.dataChanged(true);
                        this.hook('uploadedImageLoaded');
                    }
                }
            }
        },
        loadImage(imageUrl) {
            return new Promise((resolve, reject) => {
                let img = document.createElement('img');
                img.src = imageUrl;
                img.classList.add('invisible');
                img.onload = () => { resolve(); img.remove(); };
                img.onerror = () => { reject(); img.remove(); };
                this.$el.append(img);
            });
        },
        showCroppie(imageUrl) {
            this.$refs.croppieRef.bind({
                url: imageUrl
            });
            this.$refs.croppieContainer.classList.add('visible');
        },
        hideCroppie() {
            this.crop();
            this.$refs.croppieContainer.classList.remove('visible');
        },
        hook(name) {
            if(this.hooks && name in this.hooks) {
                this.hooks[name]();
            }
        },
        crop() {
            // get data for image src (base64 encoded)
            let options = {
                format: 'png', 
                circle: true,
                type: 'base64'
            }
            this.$refs.croppieRef.result(options, (output) => {
                this.imageData = output;
            });

            // get data for form (blob)
            options.type = 'blob';
            this.$refs.croppieRef.result(options, (output) => {
                this.data = output;
                this.dataChanged();
            });
        },
    }
}
</script>

<style lang="scss" scoped>
    .editable__element {
        position: relative;

        &--edit &__image {
            opacity: 0.5;
        }

        &__image-container {
            position: relative;
        }

        &__input {
            position: absolute;
            top: 50%;
            left: 0;
            right: 0;
            transform: translateY(-50%);
            text-align: center;
            font-size: map-get($font-sizes, sm);
            font-weight: map-get($font-weights, medium);
            
            > label {
                cursor: pointer;
                margin-bottom: 0;

                > input {
                    display: none;
                }
            }
        }
    }

    .croppie {
        position: fixed;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 9999;
        background-color: rgba(0, 0, 0, 0.8);

        &:not(.visible) {
            display: none;
        }

        &__header {
            font-size: map-get($font-sizes, xl);
            color: $white;
            font-weight: map-get($font-weights, medium);
            margin: map-get($spacers, md) 0;
            text-align: center;
        }

        &__controls {
            text-align: center;
        }

        .croppie-container {
            height: auto;
        }
    }
</style>